import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "@/lib/env";
import { createPortal } from "react-dom";
import {
    PDFViewer,
    PdfAnnotationSubtype,
} from "@embedpdf/react-pdf-viewer";
import type {
    PDFViewerRef,
    PluginRegistry,
    PdfHighlightAnnoObject,
    Rect,
    AnnotationTransferItem,
    ScrollPlugin,
    SearchPlugin,
    DocumentManagerPlugin,
    SelectionPlugin,
} from "@embedpdf/react-pdf-viewer";
import type { AnnotationPlugin } from "@embedpdf/react-pdf-viewer";
import { AuditFlowIcon } from "@/components/chat/auditflow-icon";
import {
    getSignedDisplayUrl,
    getPdfAnnotations,
    savePdfAnnotationsJson,
} from "@/app/lib/auditflowApi";
import type { CitationQuote } from "./types";
import type { Command } from "@embedpdf/plugin-commands";
import type { UISchema, SelectionMenuSchema } from "@embedpdf/plugin-ui";

interface Props {
    doc: { document_id: string; version_id?: string | null } | null;
    quotes?: CitationQuote[];
    quote?: string;
    fallbackPage?: number;
    rounded?: boolean;
    bordered?: boolean;
    annotatable?: boolean;
    professionalLayout?: boolean;
    /** Called when the user selects text and clicks "Ask about selection". */
    onAskAboutSelection?: (text: string) => void;
    /** Called when the user selects text and clicks "Explain". */
    onExplain?: (text: string) => void;
    /** Called when the user selects text and clicks "Summarize". */
    onSummarize?: (text: string) => void;
    /** Called when the user selects text and clicks "Rewrite". */
    onRewrite?: (text: string) => void;
}

const CITATION_ID_PREFIX = "citation-";
const CITATION_COLOR = "#FFE066";
const CITATION_OPACITY = 0.4;
const SEARCH_QUERY_MAX_LEN = 120;
const MAX_SELECTION_PREVIEW_LEN = 80;

const HIGHLIGHT_COLORS = [
    { color: "#FFE066", label: "Yellow" },
    { color: "#90EE90", label: "Green" },
    { color: "#87CEEB", label: "Blue" },
    { color: "#FFB6C1", label: "Pink" },
    { color: "#FFA500", label: "Orange" },
    { color: "#D8BFD8", label: "Purple" },
    { color: "#FF6B6B", label: "Red" },
];

function isCitationAnnotation(item: AnnotationTransferItem): boolean {
    return item.annotation.id?.startsWith(CITATION_ID_PREFIX) ?? false;
}

function computeBoundingRect(rects: Rect[]): Rect {
    if (rects.length === 0) {
        return { origin: { x: 0, y: 0 }, size: { width: 612, height: 60 } };
    }
    const minX = Math.min(...rects.map((r) => r.origin.x));
    const minY = Math.min(...rects.map((r) => r.origin.y));
    const maxX = Math.max(...rects.map((r) => r.origin.x + r.size.width));
    const maxY = Math.max(...rects.map((r) => r.origin.y + r.size.height));
    return {
        origin: { x: minX, y: minY },
        size: { width: maxX - minX, height: maxY - minY },
    };
}

/**
 * EmbedPDF viewer wrapper — drop-in replacement for DocView.
 *
 * Fetches a signed download URL from the backend and passes it to the
 * EmbedPDF <PDFViewer> component.  The WASM binary is self-hosted in
 * /static/wasm/ so the viewer works without external CDN dependencies.
 *
 * Annotation sync:
 *   - On viewer ready: load saved annotations from backend via
 *     importAnnotations()
 *   - On annotation events (create/update/delete): debounce-save the
 *     full annotation set back to the backend via exportAnnotations().
 *
 * Citation integration:
 *   - When `quotes` prop changes, scroll to the target page and inject
 *     temporary PdfHighlightAnnotation objects with IDs prefixed by
 *     "citation-".  Previous citation annotations are cleared first.
 *   - Citation annotations are never persisted to the backend.
 *
 * Text-selection → chat:
 *   - When the user highlights text, a floating "Ask about selection"
 *     button appears at the bottom of the viewer.
 *   - Clicking it calls `onAskAboutSelection(selectedText)` and clears
 *     the selection so the user can continue reading.
 */
export function EmbedPdfViewer({
    doc,
    quotes,
    quote,
    fallbackPage,
    rounded = true,
    bordered = true,
    annotatable = false,
    professionalLayout = false,
    onAskAboutSelection,
    onExplain,
    onSummarize,
    onRewrite,
}: Props) {
    const viewerRef = useRef<PDFViewerRef>(null);
    const [signedUrl, setSignedUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSavingRef = useRef(false);
    const registryRef = useRef<PluginRegistry | null>(null);
    const isApplyingCitationsRef = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);
    const pickerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const documentId = doc?.document_id ?? null;
    const versionId = doc?.version_id ?? null;

    /* ------------------------------------------------------------------ */
    /*  Signed URL loading                                                */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        if (!documentId) {
            setSignedUrl(null);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        setSignedUrl(null);

        (async () => {
            try {
                const { url } = await getSignedDisplayUrl(
                    documentId,
                    versionId ?? null,
                );
                if (!cancelled) {
                    const apiBase =
                        API_BASE_URL;
                    setSignedUrl(`${apiBase}${url}`);
                }
            } catch (err) {
                console.error("[EmbedPdfViewer] failed to get signed URL:", err);
                if (!cancelled) {
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Failed to load document.",
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [documentId, versionId]);

    /* ------------------------------------------------------------------ */
    /*  Annotation load / save                                            */
    /* ------------------------------------------------------------------ */
    const loadAnnotations = useCallback(
        async (registry: PluginRegistry) => {
            if (!documentId) return;
            try {
                const { annotations } = await getPdfAnnotations(
                    documentId,
                    versionId ?? null,
                );
                if (annotations.length > 0) {
                    const plugin = registry.getPlugin<AnnotationPlugin>("annotation");
                    if (plugin && plugin.provides) {
                        const annotationPlugin = plugin.provides();
                        // Never re-import citation annotations from backend
                        const cleanAnnotations = annotations.filter(
                            (item: any) => {
                                const id =
                                    item.annotation?.id ?? item.id ?? "";
                                return !String(id).startsWith(
                                    CITATION_ID_PREFIX,
                                );
                            },
                        );
                        annotationPlugin.importAnnotations(cleanAnnotations);
                    }
                }
            } catch (err) {
                console.error(
                    "[EmbedPdfViewer] failed to load annotations:",
                    err,
                );
            }
        },
        [documentId, versionId],
    );

    const saveAnnotations = useCallback(
        async (registry: PluginRegistry) => {
            if (!documentId || isSavingRef.current) return;
            const plugin = registry.getPlugin<AnnotationPlugin>("annotation");
            if (!plugin || !plugin.provides) return;
            const annotationPlugin = plugin.provides();

            isSavingRef.current = true;
            try {
                const items =
                    await annotationPlugin.exportAnnotations().toPromise();
                // Strip temporary citation annotations before persisting
                const userItems = items.filter(
                    (item) => !isCitationAnnotation(item),
                );
                await savePdfAnnotationsJson(
                    documentId,
                    userItems,
                    versionId ?? null,
                );
            } catch (err) {
                // Use warn so Next.js dev overlay doesn't pop for transient
                // backend issues (e.g. missing migration, network hiccup).
                console.warn(
                    "[EmbedPdfViewer] annotation save skipped:",
                    err instanceof Error ? err.message : err,
                );
            } finally {
                isSavingRef.current = false;
            }
        },
        [documentId, versionId],
    );

    const handleAnnotationEvent = useCallback(
        (registry: PluginRegistry) => {
            if (isApplyingCitationsRef.current) return;
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                void saveAnnotations(registry);
            }, 2000);
        },
        [saveAnnotations],
    );

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (pickerTimerRef.current) {
                clearTimeout(pickerTimerRef.current);
            }
        };
    }, []);

    /* ------------------------------------------------------------------ */
    /*  Citation highlight logic                                          */
    /* ------------------------------------------------------------------ */
    const applyCitationHighlights = useCallback(
        async (registry: PluginRegistry, currentQuotes: CitationQuote[]) => {
            if (isApplyingCitationsRef.current) return;
            isApplyingCitationsRef.current = true;

            try {
                const docManagerPlugin =
                    registry.getPlugin<DocumentManagerPlugin>(
                        "document-manager",
                    );
                const activeDocumentId =
                    docManagerPlugin?.provides?.().getActiveDocumentId() ??
                    null;
                if (!activeDocumentId) {
                    console.warn(
                        "[EmbedPdfViewer] No active document for citation highlights",
                    );
                    return;
                }

                const annotationPlugin =
                    registry.getPlugin<AnnotationPlugin>("annotation");
                if (!annotationPlugin?.provides) return;
                const annotationCap = annotationPlugin.provides();
                const scope = annotationCap.forDocument(activeDocumentId);

                /* 1. Export current annotations and strip old citations */
                let currentItems: AnnotationTransferItem[] = [];
                try {
                    currentItems =
                        await annotationCap.exportAnnotations().toPromise();
                } catch (err) {
                    console.error(
                        "[EmbedPdfViewer] export for citation update failed:",
                        err,
                    );
                }

                const userAnnotations = currentItems.filter(
                    (item) => !isCitationAnnotation(item),
                );

                // If no quotes to show, just re-import user annotations
                // (this clears any existing citation highlights)
                if (!currentQuotes || currentQuotes.length === 0) {
                    scope.importAnnotations(userAnnotations);
                    return;
                }

                /* 2. Scroll to the first quote's page */
                const scrollPlugin = registry.getPlugin<ScrollPlugin>("scroll");
                if (scrollPlugin?.provides) {
                    const scrollCap = scrollPlugin.provides();
                    const targetPage = Math.max(1, currentQuotes[0].page);
                    scrollCap.scrollToPage({
                        pageNumber: targetPage,
                        behavior: "smooth",
                    });
                }

                /* 3. Build new citation highlight annotations */
                const citationItems: AnnotationTransferItem[] = [];
                const searchPlugin = registry.getPlugin<SearchPlugin>("search");
                const searchCap = searchPlugin?.provides
                    ? searchPlugin.provides()
                    : null;

                const activeDoc =
                    docManagerPlugin?.provides?.().getActiveDocument();
                const pageSizes = activeDoc?.pages.map((p) => p.size) ?? [];

                for (let i = 0; i < currentQuotes.length; i++) {
                    const q = currentQuotes[i];
                    const pageIndex = Math.max(0, q.page - 1);
                    let rects: Rect[] = [];
                    let found = false;

                    if (searchCap) {
                        try {
                            const searchQuery = q.quote.slice(
                                0,
                                SEARCH_QUERY_MAX_LEN,
                            );
                            const searchTask = searchCap.searchAllPages(
                                searchQuery,
                                activeDocumentId,
                            );
                            const result = await searchTask.toPromise();
                            const pageResults = result.results.filter(
                                (r) => r.pageIndex === pageIndex,
                            );
                            if (pageResults.length > 0) {
                                rects = pageResults[0].rects;
                                found = true;
                            }
                        } catch (err) {
                            console.warn(
                                "[EmbedPdfViewer] citation search failed:",
                                err,
                            );
                        }
                    }

                    if (!found) {
                        const pageSize = pageSizes[pageIndex] ?? {
                            width: 612,
                            height: 792,
                        };
                        rects = [
                            {
                                origin: { x: 0, y: 0 },
                                size: {
                                    width: pageSize.width,
                                    height: Math.min(60, pageSize.height),
                                },
                            },
                        ];
                    }

                    const boundingRect = computeBoundingRect(rects);
                    const citationId = `${CITATION_ID_PREFIX}${i}`;

                    const highlight: PdfHighlightAnnoObject = {
                        type: PdfAnnotationSubtype.HIGHLIGHT,
                        id: citationId,
                        pageIndex,
                        rect: boundingRect,
                        segmentRects: rects,
                        opacity: CITATION_OPACITY,
                        strokeColor: CITATION_COLOR,
                        contents: q.quote,
                        flags: ["readOnly", "locked"],
                    };

                    citationItems.push({ annotation: highlight });
                }

                /* 4. Import combined annotations */
                scope.importAnnotations([...userAnnotations, ...citationItems]);
            } finally {
                // Keep the guard up briefly so the annotation-event debounce
                // doesn't fire a needless save for the imported highlights.
                setTimeout(() => {
                    isApplyingCitationsRef.current = false;
                }, 150);
            }
        },
        [],
    );

    // Respond to `quotes` array changes
    useEffect(() => {
        const registry = registryRef.current;
        if (!registry) return;
        void applyCitationHighlights(registry, quotes ?? []);
    }, [quotes, applyCitationHighlights]);

    // Respond to single `quote` prop
    useEffect(() => {
        if (!quote) return;
        const registry = registryRef.current;
        if (!registry) return;
        void applyCitationHighlights(registry, [
            { page: fallbackPage ?? 1, quote },
        ]);
    }, [quote, fallbackPage, applyCitationHighlights]);

    /* ------------------------------------------------------------------ */
    /*  Selection menu — Explain / Summarize / Rewrite / Ask               */
    /* ------------------------------------------------------------------ */
    const setupSelectionMenu = useCallback(
        (registry: PluginRegistry) => {
            const hasAny = onExplain || onSummarize || onRewrite || onAskAboutSelection;
            if (!hasAny) return;

            // Detect current viewer locale (default = en)
            const i18nPlugin = registry.getPlugin<any>("i18n");
            const locale: string =
                i18nPlugin?.provides?.().getLocale?.() ?? "en";

            const LOCALE_LABELS: Record<string, Record<string, string>> = {
                en: {
                    explain: "Explain",
                    summarize: "Summarize",
                    rewrite: "Rewrite",
                    ask: "Ask",
                },
                "pt-BR": {
                    explain: "Explicar",
                    summarize: "Resumir",
                    rewrite: "Reescrever",
                    ask: "Perguntar",
                },
                pt: {
                    explain: "Explicar",
                    summarize: "Resumir",
                    rewrite: "Reescrever",
                    ask: "Perguntar",
                },
                es: {
                    explain: "Explicar",
                    summarize: "Resumir",
                    rewrite: "Reescribir",
                    ask: "Preguntar",
                },
            };

            const t = (key: string) =>
                LOCALE_LABELS[locale]?.[key] ??
                LOCALE_LABELS["en"][key] ??
                key;

            // ── 1. Register custom commands ──
            const commandsPlugin = registry.getPlugin<any>("commands");
            if (!commandsPlugin?.provides) return;
            const cmdCap = commandsPlugin.provides();

            const makeCommand = (
                id: string,
                emoji: string,
                label: string,
                cb?: (text: string) => void,
            ): Command => ({
                id,
                label: `${emoji} ${label}`,
                categories: ["auditflow"],
                action: ({ registry: reg, documentId }) => {
                    if (!cb) return;
                    const selPlugin = reg.getPlugin<SelectionPlugin>("selection");
                    if (!selPlugin?.provides) return;
                    const textTask = selPlugin.provides().getSelectedText(documentId);
                    textTask
                        .toPromise()
                        .then((texts) => {
                            const text = texts.join(" ").trim();
                            if (text) cb(text);
                        })
                        .catch(() => {});
                },
            });

            if (onExplain)
                cmdCap.registerCommand(makeCommand("auditflow:explain", "🧠", t("explain"), onExplain));
            if (onSummarize)
                cmdCap.registerCommand(makeCommand("auditflow:summarize", "📝", t("summarize"), onSummarize));
            if (onRewrite)
                cmdCap.registerCommand(makeCommand("auditflow:rewrite", "✏️", t("rewrite"), onRewrite));
            if (onAskAboutSelection)
                cmdCap.registerCommand(
                    makeCommand("auditflow:ask-selection", "💬", t("ask"), onAskAboutSelection),
                );

            // ── 2. Merge selection menu schema ──
            const uiPlugin = registry.getPlugin<any>("ui");
            if (!uiPlugin?.provides) return;
            const uiCap = uiPlugin.provides();
            const currentSchema: UISchema = uiCap.getSchema();

            const removeIds = new Set([
                "add-highlight",
                "add-strikeout",
                "add-underline",
                "add-squiggly",
                "add-link",
                "redact-text",
            ]);

            const existingItems = currentSchema.selectionMenus?.selection?.items ?? [];
            const filteredItems = existingItems.filter(
                (item: any) => !removeIds.has(item.id),
            );

            const newItems: SelectionMenuSchema["items"] = [];
            if (onExplain)
                newItems.push({
                    type: "command-button",
                    id: "auditflow-explain-btn",
                    commandId: "auditflow:explain",
                    variant: "text",
                    categories: ["auditflow"],
                });
            if (onSummarize)
                newItems.push({
                    type: "command-button",
                    id: "auditflow-summarize-btn",
                    commandId: "auditflow:summarize",
                    variant: "text",
                    categories: ["auditflow"],
                });
            if (onRewrite)
                newItems.push({
                    type: "command-button",
                    id: "auditflow-rewrite-btn",
                    commandId: "auditflow:rewrite",
                    variant: "text",
                    categories: ["auditflow"],
                });
            if (onAskAboutSelection)
                newItems.push({
                    type: "command-button",
                    id: "auditflow-ask-btn",
                    commandId: "auditflow:ask-selection",
                    variant: "text",
                    categories: ["auditflow"],
                });

            if (newItems.length === 0) return;

            const delta: Partial<UISchema> = {
                selectionMenus: {
                    selection: {
                        id: "selection",
                        items: [
                            ...filteredItems,
                            { type: "divider", id: "auditflow-divider" },
                            ...newItems,
                        ],
                    } as SelectionMenuSchema,
                },
            };
            uiCap.mergeSchema(delta);
        },
        [onExplain, onSummarize, onRewrite, onAskAboutSelection],
    );

    /* ------------------------------------------------------------------ */
    /*  Color picker highlight                                            */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const hidePicker = () => {
            if (pickerTimerRef.current) {
                clearTimeout(pickerTimerRef.current);
                pickerTimerRef.current = null;
            }
            setPickerPos(null);
        };

        const showPickerAt = (top: number, left: number) => {
            if (pickerTimerRef.current) clearTimeout(pickerTimerRef.current);
            pickerTimerRef.current = setTimeout(() => {
                setPickerPos({ top, left });
            }, 80);
        };

        // ── Try to attach listeners inside every same-origin iframe in the container ──
        const iframeCleanups: (() => void)[] = [];

        function attachToIframe(iframe: HTMLIFrameElement) {
            try {
                const idoc = iframe.contentDocument;
                const iwin = iframe.contentWindow;
                if (!idoc || !iwin) return;

                const onMouseUp = () => {
                    const sel = iwin.getSelection();
                    const text = sel?.toString().trim() ?? "";
                    if (sel && text && sel.rangeCount > 0) {
                        const range = sel.getRangeAt(0);
                        const rects = range.getClientRects();
                        if (rects.length > 0) {
                            const lastRect = rects[rects.length - 1];
                            const iframeRect = iframe.getBoundingClientRect();
                            // viewport coords for position:fixed
                            const top = iframeRect.top + lastRect.bottom + 6;
                            const left = iframeRect.left + lastRect.left;
                            showPickerAt(top, left);
                            return;
                        }
                    }
                    hidePicker();
                };

                const onMouseDown = () => hidePicker();
                const onScroll = () => hidePicker();

                idoc.addEventListener("mouseup", onMouseUp);
                idoc.addEventListener("mousedown", onMouseDown);
                idoc.addEventListener("scroll", onScroll, { passive: true });

                iframeCleanups.push(() => {
                    idoc.removeEventListener("mouseup", onMouseUp);
                    idoc.removeEventListener("mousedown", onMouseDown);
                    idoc.removeEventListener("scroll", onScroll);
                });
            } catch {
                /* cross-origin — silently skip */
            }
        }

        // Attach to existing iframes and watch for new ones
        container.querySelectorAll("iframe").forEach(attachToIframe);

        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                for (const node of m.addedNodes) {
                    if (node instanceof HTMLIFrameElement) {
                        // iframe may not be ready immediately
                        setTimeout(() => attachToIframe(node), 200);
                    }
                }
            }
        });
        observer.observe(container, { childList: true, subtree: true });

        // ── Fallback: poll all iframes for selection (covers cross-origin / shadow-DOM) ──
        let lastSelectionText = "";
        const poll = setInterval(() => {
            try {
                const iframes = container.querySelectorAll("iframe");
                for (let i = 0; i < iframes.length; i++) {
                    const cw = (iframes[i] as HTMLIFrameElement).contentWindow;
                    if (!cw) continue;
                    const sel = cw.getSelection();
                    const text = sel?.toString().trim() ?? "";
                    if (text && text !== lastSelectionText) {
                        lastSelectionText = text;
                        if (sel && sel.rangeCount > 0) {
                            const range = sel.getRangeAt(0);
                            const rects = range.getClientRects();
                            if (rects.length > 0) {
                                const lastRect = rects[rects.length - 1];
                                const iframeRect = (iframes[i] as HTMLIFrameElement).getBoundingClientRect();
                                const top = iframeRect.top + lastRect.bottom + 6;
                                const left = iframeRect.left + lastRect.left;
                                showPickerAt(top, left);
                            }
                        }
                        return;
                    }
                }
                // No selection in any iframe — hide if we previously had one
                if (!lastSelectionText) return;
                const anyText = Array.from(iframes).some((f) => {
                    try {
                        return !!(f as HTMLIFrameElement).contentWindow?.getSelection()?.toString().trim();
                    } catch { return false; }
                });
                if (!anyText) {
                    lastSelectionText = "";
                    hidePicker();
                }
            } catch {
                /* ignore */
            }
        }, 250);

        // ── Hide picker when clicking outside it (on the main document) ──
        const docHide = (e: MouseEvent) => {
            const target = e.target as Node | null;
            const pickerEl = document.querySelector("[data-highlight-picker]");
            if (pickerEl && target && pickerEl.contains(target)) return;
            hidePicker();
        };
        document.addEventListener("mousedown", docHide);

        return () => {
            observer.disconnect();
            iframeCleanups.forEach((fn) => fn());
            clearInterval(poll);
            document.removeEventListener("mousedown", docHide);
            if (pickerTimerRef.current) clearTimeout(pickerTimerRef.current);
        };
    }, []);

    const applyHighlightColor = useCallback((color: string) => {
        const registry = registryRef.current;
        if (!registry) return;

        const docManager = registry.getPlugin<DocumentManagerPlugin>("document-manager");
        const activeDocumentId = docManager?.provides?.().getActiveDocumentId() ?? null;
        if (!activeDocumentId) return;

        const selPlugin = registry.getPlugin<SelectionPlugin>("selection");
        if (!selPlugin?.provides) return;
        const selScope = selPlugin.provides().forDocument(activeDocumentId);
        const formatted = selScope.getFormattedSelection();
        if (!formatted || formatted.length === 0) return;

        const annotationPlugin = registry.getPlugin<AnnotationPlugin>("annotation");
        if (!annotationPlugin?.provides) return;
        const annoScope = annotationPlugin.provides().forDocument(activeDocumentId);

        const selectedText = window.getSelection()?.toString() ?? "";
        const items: AnnotationTransferItem[] = [];
        for (const fs of formatted) {
            const id = `af-highlight-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            const highlight: PdfHighlightAnnoObject = {
                type: PdfAnnotationSubtype.HIGHLIGHT,
                id,
                pageIndex: fs.pageIndex,
                rect: fs.rect,
                segmentRects: fs.segmentRects,
                opacity: 0.4,
                strokeColor: color,
                contents: selectedText,
            };
            items.push({ annotation: highlight });
        }

        annoScope.importAnnotations(items);
        selScope.clear();
        setPickerPos(null);
    }, []);

    /* ------------------------------------------------------------------ */
    /*  Render                                                            */
    /* ------------------------------------------------------------------ */
    const containerClasses = [
        "relative flex flex-col h-full overflow-hidden",
        bordered ? "border border-gray-200" : "",
        rounded ? "rounded-xl" : "",
    ]
        .filter(Boolean)
        .join(" ");

    if (loading) {
        return (
            <div className={containerClasses}>
                <div className="flex h-full items-center justify-center">
                    <AuditFlowIcon spin active size={28} />
                </div>
            </div>
        );
    }

    if (error || !signedUrl) {
        return (
            <div className={containerClasses}>
                <div className="flex h-full items-center justify-center">
                    <p className="text-sm text-red-500">
                        {error ?? "Document not available."}
                    </p>
                </div>
            </div>
        );
    }

    const disabledCategories: string[] = [
        "print",
        "download",
        "document-print",
    ];
    if (!annotatable) {
        disabledCategories.push("annotation");
    }

    return (
        <div ref={containerRef} className={containerClasses}>
            <PDFViewer
                ref={viewerRef}
                config={{
                    src: signedUrl,
                    wasmUrl: `${typeof window !== "undefined" ? window.location.origin : ""}/static/wasm/pdfium.wasm`,
                    worker: false,
                    disabledCategories,
                    tabBar: "never",
                    theme: { preference: "system" },
                }}
                style={{ width: "100%", height: "100%" }}
                onReady={(registry) => {
                    registryRef.current = registry;
                    void loadAnnotations(registry);

                    const plugin = registry.getPlugin<AnnotationPlugin>("annotation");
                    if (plugin && plugin.provides) {
                        const annotationPlugin = plugin.provides();
                        annotationPlugin.onAnnotationEvent(() => {
                            handleAnnotationEvent(registry);
                        });
                    }

                    // ── Selection menu (Explain / Summarize / Rewrite) ──
                    setupSelectionMenu(registry);

                    // Apply citations that arrived before the viewer was ready
                    if (quotes && quotes.length > 0) {
                        void applyCitationHighlights(registry, quotes);
                    } else if (quote) {
                        void applyCitationHighlights(registry, [
                            { page: fallbackPage ?? 1, quote },
                        ]);
                    }
                }}
            />
            {pickerPos &&
                createPortal(
                    <div
                        data-highlight-picker
                        className="fixed flex items-center gap-2 rounded-lg bg-gray-900/95 shadow-xl border border-gray-700 px-3 py-2 z-[9999]"
                        style={{
                            top: pickerPos.top,
                            left: pickerPos.left,
                            transform: "translateX(-25%)",
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        {HIGHLIGHT_COLORS.map((c) => (
                            <button
                                key={c.color}
                                title={c.label}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    applyHighlightColor(c.color);
                                }}
                                className="w-6 h-6 rounded-full border-2 border-white/20 hover:scale-125 hover:border-white transition-all cursor-pointer shadow-sm"
                                style={{ backgroundColor: c.color }}
                            />
                        ))}
                    </div>,
                    document.body,
                )}
        </div>
    );
}
